'use strict';

// Per-device cache of the ENCRYPTED vault (+ salt/kdf) in IndexedDB. This is what
// makes "remember this device" and offline unlock work: the password is still
// required to derive the vault key, so the cached blob is useless on its own.
// Phase 5 will wrap recall() behind a WebAuthn biometric check.

const DB_NAME = 'web3keys';
const STORE = 'devices';
const BIO = 'biometric';
const VERSION = 2;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'email' });
      if (!db.objectStoreNames.contains(BIO)) db.createObjectStore(BIO, { keyPath: 'email' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const norm = (e) => String(e || '').toLowerCase();

async function remember(email, data) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ email: norm(email), salt: data.salt, kdf: data.kdf, vaultBlob: data.vaultBlob, savedAt: Date.now() });
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

async function recall(email) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(norm(email));
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function forget(email) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(norm(email));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

async function listRemembered() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve((req.result || []).map((r) => r.email));
    req.onerror = () => reject(req.error);
  });
}

// --- biometric (WebAuthn PRF) enrollment per device ---
async function rememberBiometric(email, enrollment) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BIO, 'readwrite');
    tx.objectStore(BIO).put({ email: norm(email), credentialId: enrollment.credentialId, wrapped: enrollment.wrapped, savedAt: Date.now() });
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}
async function recallBiometric(email) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BIO, 'readonly');
    const req = tx.objectStore(BIO).get(norm(email));
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}
async function forgetBiometric(email) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BIO, 'readwrite');
    tx.objectStore(BIO).delete(norm(email));
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

module.exports = { remember, recall, forget, listRemembered, rememberBiometric, recallBiometric, forgetBiometric };
